import { memo, useId, useMemo, useState } from 'react';

import { Collapse } from '@material-ui/core';

import { RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { Text } from '@spotify-internal/encore-web';

import { DisplayItem } from '../../hooks/groupChatEvents';
import {
  AgentProgress,
  type AgentProgressItem,
  AgentProgressItems,
  toAgentProgressItems,
} from './AgentProgress';

import styles from './ThoughtProcess.module.css';

type ThoughtProcessProps = {
  items: DisplayItem[];
  isActive: boolean;
  isInterrupted: boolean;
  // Whether the completed thought process starts expanded. Defaults to collapsed
  // for the main thread (where it is a low-priority recap); surfaces that make
  // the reasoning the primary content (e.g. a coding session transcript) pass true.
  defaultExpanded?: boolean;
};

export const ThoughtProcess = memo(
  ({
    items,
    isActive,
    isInterrupted,
    defaultExpanded = false,
  }: ThoughtProcessProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const panelId = useId();
    const progressItems = useMemo<AgentProgressItem[]>(
      () => toAgentProgressItems(items),
      [items],
    );

    if (isActive) {
      return (
        <AgentProgress items={progressItems} isInterrupted={isInterrupted} />
      );
    }

    const headerLabel = isInterrupted
      ? 'Thought process (interrupted)'
      : 'Thought process';

    return (
      <div>
        <button
          type="button"
          className={styles.header}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => {
            setExpanded(prev => !prev);
          }}
        >
          <Text semanticColor="textSubdued">{headerLabel}</Text>
          <RiArrowRightSLine
            size={16}
            className={classNames(styles.arrow, {
              [styles.arrowExpanded]: expanded,
            })}
          />
        </button>
        <Collapse in={expanded}>
          <div id={panelId}>
            <div className={styles.content}>
              <AgentProgressItems
                items={progressItems}
                shimmerLatestThinking={false}
              />
            </div>
          </div>
        </Collapse>
      </div>
    );
  },
);
ThoughtProcess.displayName = 'ThoughtProcess';
